import {TxHistoryOrderLogsMeta, TxHistoryRec, TxHistoryRecMethod, TxHistoryStatus} from '../tx-history-rec';
import {TransactionApiModel} from './transaction-api.model';

interface ApiTxMetadata {
    to: TxHistoryRec['to'];
    blockNumber: TxHistoryRec['blockNumber'];
    metadata: TxHistoryRec['metadata'];
    logs: TxHistoryRec['logs'];
    logsMeta: TxHistoryRec['logsMeta'];
    gasUsedNative: TxHistoryRec['gasUsedNative'];
    failureReason: TxHistoryRec['failureReason'];
}

export const jsonStringifyBigIntReplacer = (_key: string, value: any) =>
    typeof value === 'bigint' ? value.toString() : value;

/**
 * Converts TransactionApiModel (from backend) to TxHistoryRec (used in UI)
 */
export function txHistoryRecFromTransactionApi(apiTx: TransactionApiModel): TxHistoryRec {
    const apiTxMetadata: ApiTxMetadata | undefined = apiTx.metadata ? JSON.parse(apiTx.metadata) : undefined;

    return {
        hash:                 apiTx.hash ?? undefined,
        from:                 apiTx.from ?? undefined,
        to:                   apiTxMetadata?.to ?? undefined,
        data:                 apiTx.data ?? undefined,
        value:                apiTx.value ?? undefined,
        nonce:                apiTx.nonce ?? undefined,
        gasLimit:             apiTx.gasLimit ?? undefined,
        gasPrice:             apiTx.gasPrice ?? undefined,
        maxFeePerGas:         apiTx.maxFeePerGas ?? undefined,
        maxPriorityFeePerGas: apiTx.maxPriorityFeePerGas ?? undefined,
        chainId:              apiTx.chainId ?? undefined,
        blockNumber:          apiTxMetadata?.blockNumber ?? undefined,
        status:               apiTx.status || TxHistoryStatus.PENDING,
        createdAt:            apiTx.createdAt ? new Date(apiTx.createdAt).valueOf() : undefined,
        updatedAt:            apiTx.updatedAt ? new Date(apiTx.updatedAt).valueOf() : undefined,
        metadata:             apiTxMetadata?.metadata ?? undefined,
        logs:                 apiTxMetadata?.logs ?? undefined,
        logsMeta:             apiTxMetadata?.logsMeta ?? undefined,
        gasUsedNative:        apiTxMetadata?.gasUsedNative ?? undefined,
        failureReason:        apiTxMetadata?.failureReason ?? undefined
    };
}

/**
 * Converts TxHistoryRec (UI model) to TransactionApiModel (for backend)
 */
export function transactionApiFromTxHistoryRec(tx: TxHistoryRec): TransactionApiModel {
    const apiTxMetadata: ApiTxMetadata = {
        to:            tx.to,
        blockNumber:   tx.blockNumber,
        metadata:      tx.metadata,
        logs:          tx.logs,
        logsMeta:      tx.logsMeta,
        gasUsedNative: tx.gasUsedNative,
        failureReason: tx.failureReason
    };

    const isOrderTx = !!tx.metadata && (
        tx.metadata.method === TxHistoryRecMethod.DEPOSIT
        || tx.metadata.method === TxHistoryRecMethod.WITHDRAW
    );

    const orderId: string | null = isOrderTx
        ? (tx.logsMeta as TxHistoryOrderLogsMeta)?.orderEnteredEvent?.orderId || null
        : null;

    const hasTxReceipt = tx.status !== TxHistoryStatus.PENDING;

    /**
     * Tells that the transaction is related to an order,
     * but the orderId may not be yet resolved in the transaction.
     * If the transaction is already mined, but the orderId is not yet resolved,
     * then tell the backend that the transaction will not produce an order.
     */
    const hasOrder: boolean = isOrderTx && (!!orderId || !hasTxReceipt);

    return {
        id:     undefined, // Should be set by API
        userId: undefined, // Should be set by API

        hash:                 tx.hash ?? null,
        type:                 tx.metadata?.method ?? null,
        from:                 tx.from ?? null,
        data:                 tx.data ?? null,
        value:                tx.value ?? null,
        nonce:                tx.nonce ?? null,
        gasLimit:             tx.gasLimit ?? null,
        gasPrice:             tx.gasPrice ?? null,
        maxFeePerGas:         tx.maxFeePerGas ?? null,
        maxPriorityFeePerGas: tx.maxPriorityFeePerGas ?? null,
        chainId:              tx.chainId ?? null,
        status:               tx.status ?? null,
        createdAt:            tx.createdAt ? new Date(tx.createdAt).toISOString() : null,
        updatedAt:            tx.updatedAt ? new Date(tx.updatedAt).toISOString() : null,
        metadata:             JSON.stringify(apiTxMetadata, jsonStringifyBigIntReplacer),
        orderId:              (tx.logsMeta as TxHistoryOrderLogsMeta)?.orderEnteredEvent?.orderId || null,
        hasOrder
    };
}
